import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import {
  getActiveCart,
  getAvailableProducts,
  getSeedBookingErrorMessage,
  removeCartItem,
  saveCartItem,
} from "./SeedBookingApi";
import {
  EmptyState,
  LoadingState,
  PageHeader,
  formatAmount,
  getSeedBookingDistributorName,
} from "./SeedBookingUi";
import "./SeedBooking.css";

function cartItemDrafts(cart) {
  return Object.fromEntries(
    (cart?.items || []).map((item) => [
      item.productItemId,
      { quantity: String(item.quantity), totalCost: String(item.totalCost) },
    ]),
  );
}

function isDraftDirty(draft, cartItem) {
  if (!draft) return false;
  if (!cartItem) return Boolean(draft.quantity.trim() || draft.totalCost.trim());
  return Number(draft.quantity) !== Number(cartItem.quantity)
    || Number(draft.totalCost) !== Number(cartItem.totalCost);
}

const SeedBookingDashboard = () => {
  const userType = localStorage.getItem("userType");
  const isBpUser = userType === "BPUser";
  const [distributorId] = useState(localStorage.getItem("distributorId") || "");
  const distributorName = isBpUser ? getSeedBookingDistributorName(distributorId) : "";
  const [catalog, setCatalog] = useState([]);
  const [cart, setCart] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [savingItemIds, setSavingItemIds] = useState([]);

  useEffect(() => {
    if (!distributorId) {
      setCatalog([]);
      setCart(null);
      setDrafts({});
      return undefined;
    }

    let active = true;
    setLoadingCatalog(true);
    Promise.allSettled([
      getAvailableProducts(distributorId),
      getActiveCart(distributorId),
    ])
      .then(([productsResult, cartResult]) => {
        if (!active) return;

        if (productsResult.status === "fulfilled") {
          setCatalog(Array.isArray(productsResult.value) ? productsResult.value : []);
        } else {
          setCatalog([]);
          toast.error(getSeedBookingErrorMessage(productsResult.reason));
        }

        if (cartResult.status === "fulfilled") {
          setCart(cartResult.value);
          setDrafts(cartItemDrafts(cartResult.value));
        } else {
          setCart(null);
          setDrafts({});
          toast.error(getSeedBookingErrorMessage(cartResult.reason));
        }
      })
      .finally(() => active && setLoadingCatalog(false));

    return () => {
      active = false;
    };
  }, [distributorId]);

  const cartItemsByProductId = Object.fromEntries(
    (cart?.items || []).map((item) => [item.productItemId, item]),
  );
  const search = searchTerm.trim().toLocaleLowerCase();
  // Only the display name is shown, so search only that (the product name when no display name is set).
  const matches = (entry) =>
    (entry.displayName || entry.name || "").toLocaleLowerCase().includes(search);
  const visibleProductLines = catalog
    .map((line) => ({
      ...line,
      productItems: !search || matches(line)
        ? line.productItems
        : line.productItems.filter(matches),
    }))
    .filter((line) => line.productItems.length > 0);
  const pageBase = isBpUser ? "/BPUDashboard/SeedBooking" : "/SeedBooking";
  const cartCount = cart?.items?.length || 0;
  const unsavedCount = catalog
    .flatMap((line) => line.productItems)
    .filter((product) => isDraftDirty(drafts[product.id], cartItemsByProductId[product.id]))
    .length;

  const updateDraft = (productItemId, field, value) => {
    setDrafts((current) => ({
      ...current,
      [productItemId]: {
        ...(current[productItemId] || { quantity: "", totalCost: "" }),
        [field]: value,
      },
    }));
  };

  const saveItem = async (productItemId) => {
    const draft = drafts[productItemId] || { quantity: "", totalCost: "" };
    const quantity = Number(draft.quantity);
    const totalCost = Number(draft.totalCost);

    if (!draft.quantity.trim() || !Number.isFinite(quantity) || quantity <= 0) {
      toast.error("Enter Qtl greater than zero.");
      return;
    }
    if (!draft.totalCost.trim() || !Number.isFinite(totalCost) || totalCost < 0) {
      toast.error("Enter a valid amount for this item.");
      return;
    }

    setSavingItemIds((current) => [...current, productItemId]);
    try {
      const updatedCart = await saveCartItem({
        distributorId: Number(distributorId),
        productItemId,
        quantity,
        totalCost,
      });
      setCart(updatedCart);
      setDrafts((current) => ({
        ...current,
        [productItemId]: { quantity: String(quantity), totalCost: String(totalCost) },
      }));
      toast.success("Item saved to cart.");
    } catch (error) {
      toast.error(getSeedBookingErrorMessage(error));
    } finally {
      setSavingItemIds((current) => current.filter((id) => id !== productItemId));
    }
  };

  const removeItem = async (productItemId, cartItemId) => {
    setSavingItemIds((current) => [...current, productItemId]);
    try {
      setCart(await removeCartItem({
        distributorId: Number(distributorId),
        cartItemId,
      }));
      setDrafts((current) => {
        const next = { ...current };
        delete next[productItemId];
        return next;
      });
      toast.success("Item removed from cart.");
    } catch (error) {
      toast.error(getSeedBookingErrorMessage(error));
    } finally {
      setSavingItemIds((current) => current.filter((id) => id !== productItemId));
    }
  };

  const handleFieldKeyDown = (event, productItemId) => {
    if (event.key === "Enter") {
      event.preventDefault();
      saveItem(productItemId);
    }
  };

  return (
    <section className="content seed-booking-page">
      <div className="container-fluid">
        <PageHeader
          title="Seed Booking"
          subtitle="Enter Qtl and amount for each product you want to book."
          distributorName={distributorName}
        />

        {!distributorId ? (
          <EmptyState icon="fa-user-circle" title="No distributor selected">
            {isBpUser
              ? "Open Seed Booking from a distributor on your dashboard to start a booking."
              : "A distributor account is required to start a seed booking."}
            {isBpUser && (
              <Link to="/BPUDashboard/BPUDashContent" className="btn sb-btn sb-btn-primary">
                Go to dashboard
              </Link>
            )}
          </EmptyState>
        ) : loadingCatalog ? (
          <LoadingState label="Loading products..." />
        ) : (
          <>
            <div className="sb-toolbar">
              <div className="sb-search">
                <i className="fa fa-search" aria-hidden="true"></i>
                <input
                  type="search"
                  className="form-control"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search crop or product"
                  aria-label="Search crop or product"
                />
              </div>
              <span className="sb-toolbar-meta">
                {visibleProductLines.length} of {catalog.length} crops
              </span>
            </div>

            {visibleProductLines.length === 0 ? (
              <EmptyState icon="fa-leaf" title={catalog.length === 0 ? "No products available" : "No matches"}>
                {catalog.length === 0
                  ? "No products are currently enabled for seed booking."
                  : "No crop or product matches your search."}
              </EmptyState>
            ) : (
              <div className="sb-lines">
                {visibleProductLines.map((line) => {
                  const inCartCount = line.productItems.filter((product) => cartItemsByProductId[product.id]).length;
                  return (
                    <div key={line.id} className="sb-card sb-line">
                      <div className="sb-line-header">
                        <div>
                          <h2>{line.displayName || line.name}</h2>
                        </div>
                        {inCartCount > 0 && (
                          <span className="sb-pill">
                            <i className="fa fa-shopping-cart" aria-hidden="true"></i>
                            {inCartCount} in cart
                          </span>
                        )}
                      </div>

                      <div className="sb-item-head" aria-hidden="true">
                        <span>Product</span>
                        <span>Qtl</span>
                        <span>Booking Amount (₹)</span>
                        <span></span>
                      </div>

                      {line.productItems.map((product) => {
                        const cartItem = cartItemsByProductId[product.id];
                        const draft = drafts[product.id] || {
                          quantity: cartItem ? String(cartItem.quantity) : "",
                          totalCost: cartItem ? String(cartItem.totalCost) : "",
                        };
                        const dirty = isDraftDirty(drafts[product.id], cartItem);
                        const saving = savingItemIds.includes(product.id);
                        const productName = product.displayName || product.name;

                        return (
                          <div
                            key={product.id}
                            className={`sb-item${cartItem ? " is-in-cart" : ""}${dirty ? " is-dirty" : ""}`}
                          >
                            <div className="sb-item-name">
                              <span className="sb-item-title">{productName}</span>
                              {cartItem && !dirty && (
                                <span className="sb-item-tag sb-item-tag-saved">
                                  <i className="fa fa-check" aria-hidden="true"></i> In cart
                                </span>
                              )}
                              {dirty && (
                                <span className="sb-item-tag sb-item-tag-dirty">Not saved</span>
                              )}
                            </div>
                            <label className="sb-field">
                              <span>Qtl</span>
                              <input
                                type="text"
                                inputMode="decimal"
                                className="form-control"
                                placeholder="0"
                                aria-label={`Qtl for ${productName}`}
                                value={draft.quantity}
                                onChange={(event) => updateDraft(product.id, "quantity", event.target.value)}
                                onKeyDown={(event) => handleFieldKeyDown(event, product.id)}
                                disabled={saving}
                              />
                            </label>
                            <label className="sb-field">
                              <span>Booking Amount (₹)</span>
                              <input
                                type="text"
                                inputMode="decimal"
                                className="form-control"
                                placeholder="0.00"
                                aria-label={`Amount for ${productName}`}
                                value={draft.totalCost}
                                onChange={(event) => updateDraft(product.id, "totalCost", event.target.value)}
                                onKeyDown={(event) => handleFieldKeyDown(event, product.id)}
                                disabled={saving}
                              />
                            </label>
                            <div className="sb-item-actions">
                              <button
                                type="button"
                                className={`btn sb-btn ${cartItem && !dirty ? "sb-btn-ghost" : "sb-btn-primary"}`}
                                onClick={() => saveItem(product.id)}
                                disabled={saving || (cartItem && !dirty)}
                              >
                                {saving ? (
                                  <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
                                ) : cartItem ? (
                                  dirty ? "Update" : <><i className="fa fa-check" aria-hidden="true"></i> Added</>
                                ) : (
                                  <><i className="fa fa-plus" aria-hidden="true"></i> Add</>
                                )}
                              </button>
                              {cartItem && (
                                <button
                                  type="button"
                                  className="btn sb-btn sb-btn-icon"
                                  onClick={() => removeItem(product.id, cartItem.id)}
                                  disabled={saving}
                                  title="Remove from cart"
                                  aria-label={`Remove ${productName} from cart`}
                                >
                                  <i className="fa fa-trash-o" aria-hidden="true"></i>
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="sb-cartbar">
              <div className="sb-cartbar-summary">
                <span className="sb-cartbar-icon" aria-hidden="true">
                  <i className="fa fa-shopping-cart"></i>
                  {cartCount > 0 && <span className="sb-cartbar-count">{cartCount}</span>}
                </span>
                <div>
                  <strong>{formatAmount(cart?.subTotal)}</strong>
                  <small>
                    {cartCount} {cartCount === 1 ? "item" : "items"} in cart
                    {unsavedCount > 0 && (
                      <span className="sb-cartbar-warning"> · {unsavedCount} not saved</span>
                    )}
                  </small>
                </div>
              </div>
              <Link
                to={`${pageBase}/CartSummary`}
                className={`btn sb-btn sb-btn-primary${cartCount ? "" : " disabled"}`}
                aria-disabled={!cartCount}
                onClick={(event) => !cartCount && event.preventDefault()}
              >
                Review cart <i className="fa fa-arrow-right" aria-hidden="true"></i>
              </Link>
            </div>
          </>
        )}
      </div>
      <ToastContainer position="bottom-center" autoClose={3500} limit={2} />
    </section>
  );
};

export default SeedBookingDashboard;
